import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import type {
  ShopOverrideRuleDto,
  ShopOverrideRuleActionDto,
  CreateShopOverrideRuleDto,
  UpdateShopOverrideRuleDto,
} from "@babackapp/private-api-client/types";
import {
  FieldIdDefinition,
  type OverrideRuleFormValuesInput,
} from "@/lib/override-rules";
import { OverrideRuleForm } from "./override-rule-form";
import { useOverrideRules } from "@/components/override-rules/override-rules-context.tsx";
import useTranslation from "@babackapp/translations/use-translation";
import type { CarrierOption } from "./scope-selector";
import type { ScopeFormValues } from "@/lib/override-rules";
import { countryFromISO, flagEmoji } from "@/utils/country";
import type { ReactNode } from "react";

function CarrierBadge({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      {name.toUpperCase()}
    </span>
  );
}

interface OverrideRuleDialogLabels {
  createTitle?: string;
  editTitle?: string;
}

interface OverrideRuleDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  existingRule?: ShopOverrideRuleDto;
  actionType: ShopOverrideRuleActionDto["type"];
  actionValueOptions?: { label: string; key: string }[];
  onSuccess?: (rules: ShopOverrideRuleDto[]) => void;
  excludeFields?: FieldIdDefinition[];
  labels?: OverrideRuleDialogLabels;
  showScope?: boolean;
  carriers?: CarrierOption[];
  defaultScope?: ScopeFormValues;
}

function buildScopeDetails(
  scope: ScopeFormValues,
  carriers: CarrierOption[],
): ReactNode {
  switch (scope.level) {
    case "GLOBAL":
      return null;
    case "CARRIER": {
      const carrier = carriers.find((c) => c.id === scope.carrierId);
      if (!carrier) return scope.carrierId ?? null;
      return <CarrierBadge name={carrier.name} />;
    }
    case "TARIF": {
      const carrier = carriers.find((c) => c.id === scope.carrierId);
      if (!carrier) return scope.carrierId ?? null;
      const pricing = carrier.pricings.find((p) => p.id === scope.pricingId);
      const routeParts: string[] = [];
      if (pricing?.origin)
        routeParts.push(
          `${flagEmoji(pricing.origin)} ${countryFromISO(pricing.origin)}`,
        );
      if (pricing?.destination)
        routeParts.push(
          `${flagEmoji(pricing.destination)} ${countryFromISO(pricing.destination)}`,
        );
      const routeText = routeParts.join(" → ");
      const priceText =
        pricing?.price != null && pricing.currency
          ? `· ${pricing.price} ${pricing.currency}`
          : "";
      return (
        <span className="flex flex-wrap items-center gap-1.5">
          <CarrierBadge name={carrier.name} />
          {routeText && (
            <span>
              · {routeText} {priceText}
            </span>
          )}
        </span>
      );
    }
  }
}

export function OverrideRuleDialog({
  isOpen,
  onOpenChange,
  existingRule,
  actionType,
  actionValueOptions,
  excludeFields,
  labels,
  showScope,
  carriers,
  defaultScope,
}: OverrideRuleDialogProps) {
  const t = useTranslation();
  const {
    rules,
    createRule,
    createRulePending,
    updateRules,
    updateRulesPending,
  } = useOverrideRules();
  const isEditing = !!existingRule;

  const isLoading = createRulePending || updateRulesPending;

  // Effective scope: explicitly provided, or read from the existing rule being edited
  const effectiveScope: ScopeFormValues | null =
    defaultScope ?? (existingRule?.scope as ScopeFormValues | null) ?? null;

  const level = effectiveScope?.level.toLowerCase() as
    | "global"
    | "carrier"
    | "tarif"
    | undefined;

  const scopeTitle: string | null = level
    ? t(`overrideRules.scope.${level}Title` as Parameters<typeof t>[0])
    : null;

  // First subtitle line: scope details (carrier badge, route, price)
  const scopeDetails: ReactNode =
    effectiveScope && carriers
      ? buildScopeDetails(effectiveScope, carriers)
      : null;

  // Second subtitle line: static contextual description per scope level
  const scopeSubtitle: string | null = level
    ? t(`overrideRules.scope.${level}Subtitle` as Parameters<typeof t>[0])
    : null;

  const handleSubmit = (data: OverrideRuleFormValuesInput) => {
    if (isEditing && existingRule) {
      const updateData = {
        id: existingRule.id,
        name: data.name,
        description: data.description,
        isActive: data.isActive ?? true,
        priority: existingRule.priority,
        conditions: data.conditions,
        action: data.action,
        scope: data.scope ?? null,
      } as UpdateShopOverrideRuleDto;
      updateRules([updateData], { onSuccess: () => onOpenChange(false) });
    } else {
      const createData = {
        name: data.name,
        description: data.description,
        isActive: data.isActive ?? true,
        priority:
          rules.filter((rule) => rule.action?.type === actionType).length + 1,
        conditions: data.conditions,
        action: data.action,
        scope: data.scope ?? null,
      } as CreateShopOverrideRuleDto;
      createRule(createData, { onSuccess: () => onOpenChange(false) });
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {scopeTitle ??
              (isEditing
                ? (labels?.editTitle ?? t("overrideRules.misc.editTitle"))
                : (labels?.createTitle ?? t("overrideRules.misc.createTitle")))}
          </DialogTitle>
          {scopeDetails && (
            <div className="text-sm text-muted-foreground">{scopeDetails}</div>
          )}
          {scopeSubtitle && (
            <p className="text-sm text-muted-foreground">{scopeSubtitle}</p>
          )}
        </DialogHeader>

        <OverrideRuleForm
          existingRule={existingRule}
          actionType={actionType}
          actionValueOptions={actionValueOptions}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isLoading}
          excludeFields={excludeFields}
          showScope={effectiveScope ? false : showScope}
          carriers={carriers}
          defaultScope={defaultScope}
        />
      </DialogContent>
    </Dialog>
  );
}
